import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const expired = params.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      const redirect = sessionStorage.getItem("tenx_post_auth_redirect");
      if (redirect && user.role === "investor") {
        navigate(redirect);
      } else {
        navigate(user.role === "founder" ? "/app" : "/portal");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const sendMagicLink = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    setMagicLoading(true);
    try {
      await requestMagicLink(email);
      setMagicSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the sign-in link");
    } finally { setMagicLoading(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Logo />
          <h1 className="h-display mt-8 text-2xl font-semibold text-foreground">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome back. Keep your investors in the loop.</p>

          {expired && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="session-expired-note">
              Your session expired for security. Please sign in again.
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="mt-1" data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="mt-1" data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">or</span></div>
          </div>

          {magicSent ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="magic-sent-note">
              Check your inbox - if <b>{email}</b> has a tenx account, a one-time sign-in link is on its way. It expires in 15 minutes.
            </div>
          ) : (
            <Button type="button" variant="outline" className="w-full gap-2" disabled={magicLoading} onClick={sendMagicLink} data-testid="magic-link-btn">
              {magicLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me a sign-in link
            </Button>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New here? <Link to="/register" className="font-medium text-primary hover:underline" data-testid="go-register">Create an account</Link>
          </p>
        </div>
      </div>

      <div className="relative hidden overflow-hidden bg-sidebar lg:block">
        <div className="tenx-grid-cream absolute inset-0" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <span className="ff-mono text-[10px] font-medium uppercase tracking-[0.16em] text-white/65">tenx / command center</span>
          <div>
            <h2 className="ff-cabinet max-w-md text-4xl font-black uppercase leading-[0.95] tracking-tight text-white">
              One system for the <span className="text-brand">entire raise.</span>
            </h2>
            <p className="ff-manrope mt-4 max-w-sm text-sm font-medium leading-relaxed text-white/65">
              Cap tables, tracked decks, AI investor updates, capital calls and LP reporting. All talking to each other.
            </p>
            <div className="mt-8 grid max-w-sm grid-cols-3 gap-2">
              {[["NAV", "$12.4M"], ["TVPI", "1.84x"], ["OPEN RATE", "92%"]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
                  <div className="ff-mono text-[9px] uppercase tracking-[0.14em] text-white/65">{k}</div>
                  <div className="ff-cabinet mt-1 text-lg font-extrabold text-brand">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <span className="ff-mono text-[10px] uppercase tracking-[0.16em] text-white/65">© {new Date().getFullYear()} TenX, Inc.</span>
        </div>
      </div>
    </div>
  );
}
